'use client';
import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
import { useEffect, useState } from 'react';
import { AnimatePresence, motion } from 'motion/react';
/**
 * Professional "connecting to an agent" screen.
 *
 * The agent's avatar sits centred inside a slim spinning gradient ring while we
 * wait, with a rotating status line and a thin progress bar. The moment the
 * agent connects the parent flips `caught` to true: the ring settles into a
 * solid ring, a check badge pops onto the avatar, and after a beat we fire
 * `onComplete` so the parent can fade into the live chat.
 *
 * Dismissal is NEVER driven by a fixed timer inside this component — the parent
 * ties `caught` to the real agent-connected event (first message), with its own
 * fallback timeout for slow connections.
 */
// Placeholder brand colors — swap these for the host site's palette.
const BRAND = {
    primary: '#7C3AED',
    accent: '#22D3EE',
    soft: '#EDE9FE',
    track: '#EEEAF8',
    ink: '#111827',
    online: '#22C55E',
};
const CONNECTING_MESSAGES = [
    'Connecting you to an agent…',
    'Almost there grabbing someone to help!',
    "Hang tight, this'll just take a moment 😊",
];
const CAUGHT_MESSAGE = 'Connected — starting your chat';
// Time the finishing animation gets to play before we hand off to the chat.
const FINISH_MS = 1100;
const AVATAR_SIZE = 72;
const RING_SIZE = 88;
export function ConnectingLoader({ caught, onComplete, avatarSrc, name, className, }) {
    var _a, _b;
    const [messageIndex, setMessageIndex] = useState(0);
    const [progress, setProgress] = useState(8);
    // Rotate the warm status messages every ~1.2s while still connecting.
    useEffect(() => {
        if (caught)
            return;
        const id = setInterval(() => {
            setMessageIndex((i) => (i + 1) % CONNECTING_MESSAGES.length);
        }, 1200);
        return () => clearInterval(id);
    }, [caught]);
    // Subtle progress feedback: ease toward ~90% while connecting, snap to 100%
    // once the agent is connected.
    useEffect(() => {
        if (caught) {
            setProgress(100);
            return;
        }
        const id = setInterval(() => {
            setProgress((p) => (p < 90 ? p + (90 - p) * 0.08 + 0.4 : p));
        }, 120);
        return () => clearInterval(id);
    }, [caught]);
    // Play the finishing animation, then tell the parent to dismiss.
    useEffect(() => {
        if (!caught)
            return;
        const id = setTimeout(onComplete, FINISH_MS);
        return () => clearTimeout(id);
    }, [caught, onComplete]);
    const monogram = ((_b = (_a = name === null || name === void 0 ? void 0 : name.trim()) === null || _a === void 0 ? void 0 : _a[0]) !== null && _b !== void 0 ? _b : 'A').toUpperCase();
    return (_jsxs(motion.div, { initial: { opacity: 0 }, animate: { opacity: 1 }, exit: { opacity: 0 }, transition: { duration: 0.3, ease: 'easeOut' }, className: className, style: {
            position: 'absolute',
            inset: 0,
            display: 'flex',
            flexDirection: 'column',
            alignItems: 'center',
            justifyContent: 'center',
            gap: 22,
            padding: 24,
            background: '#FFFFFF',
            overflow: 'hidden',
        }, role: "status", "aria-live": "polite", "aria-label": caught ? CAUGHT_MESSAGE : CONNECTING_MESSAGES[messageIndex], children: [_jsx(StyleTag, {}), _jsxs("div", { style: { display: 'grid', placeItems: 'center' }, children: [_jsx("div", { "aria-hidden": "true", className: caught ? 'lkcl-ring lkcl-ring--done' : 'lkcl-ring', style: { gridArea: '1 / 1', width: RING_SIZE, height: RING_SIZE } }), _jsxs("div", { style: {
                            gridArea: '1 / 1',
                            position: 'relative',
                            width: AVATAR_SIZE,
                            height: AVATAR_SIZE,
                        }, children: [avatarSrc ? (
                            // eslint-disable-next-line @next/next/no-img-element
                            _jsx("img", { src: avatarSrc, alt: name || 'Agent', style: {
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    objectFit: 'cover',
                                    display: 'block',
                                } })) : (_jsx("div", { style: {
                                    width: '100%',
                                    height: '100%',
                                    borderRadius: '50%',
                                    display: 'flex',
                                    alignItems: 'center',
                                    justifyContent: 'center',
                                    background: BRAND.primary,
                                    color: '#fff',
                                    fontSize: 26,
                                    fontWeight: 600,
                                }, children: monogram })), _jsx(AnimatePresence, { children: caught && (_jsx(motion.div, { initial: { scale: 0, opacity: 0 }, animate: { scale: 1, opacity: 1 }, transition: { type: 'spring', stiffness: 500, damping: 22 }, style: {
                                        position: 'absolute',
                                        right: -1,
                                        bottom: -1,
                                        width: 24,
                                        height: 24,
                                        borderRadius: '50%',
                                        background: BRAND.online,
                                        border: '2.5px solid #fff',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                    }, children: _jsx("svg", { width: "12", height: "12", viewBox: "0 0 24 24", fill: "none", "aria-hidden": "true", children: _jsx("path", { d: "M5 13l4 4L19 7", stroke: "#fff", strokeWidth: "3", strokeLinecap: "round", strokeLinejoin: "round" }) }) }, "check")) })] })] }), _jsx("div", { style: {
                    height: 22,
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    width: '100%',
                }, children: _jsx(AnimatePresence, { mode: "wait", children: _jsx(motion.p, { initial: { opacity: 0, y: 5 }, animate: { opacity: 1, y: 0 }, exit: { opacity: 0, y: -5 }, transition: { duration: 0.25 }, style: {
                            margin: 0,
                            fontSize: 15,
                            fontWeight: 600,
                            color: BRAND.ink,
                            textAlign: 'center',
                            lineHeight: 1.4,
                        }, children: caught ? CAUGHT_MESSAGE : CONNECTING_MESSAGES[messageIndex] }, caught ? 'caught-msg' : messageIndex) }) }), _jsx("div", { "aria-hidden": "true", style: {
                    width: 168,
                    height: 4,
                    borderRadius: 999,
                    background: BRAND.track,
                    overflow: 'hidden',
                }, children: _jsx("div", { style: {
                        height: '100%',
                        width: `${progress}%`,
                        borderRadius: 999,
                        background: `linear-gradient(90deg, ${BRAND.primary}, ${BRAND.accent})`,
                        transition: 'width 0.4s ease-out',
                    } }) })] }, "connecting-loader"));
}
function StyleTag() {
    // The ring is a conic gradient masked into a thin annulus, so it hugs the
    // avatar precisely and spins smoothly while connecting.
    return (_jsx("style", { children: `
      .lkcl-ring {
        border-radius: 50%;
        background: conic-gradient(
          from 0deg,
          ${BRAND.track} 0deg,
          ${BRAND.track} 90deg,
          ${BRAND.accent} 200deg,
          ${BRAND.primary} 330deg,
          ${BRAND.track} 360deg
        );
        -webkit-mask: radial-gradient(farthest-side, #0000 calc(100% - 3px), #000 calc(100% - 3px));
                mask: radial-gradient(farthest-side, #0000 calc(100% - 3px), #000 calc(100% - 3px));
        animation: lkcl-spin 1s linear infinite;
      }
      .lkcl-ring--done {
        background: ${BRAND.primary};
        animation: none;
      }
      @keyframes lkcl-spin {
        to { transform: rotate(360deg); }
      }
      @media (prefers-reduced-motion: reduce) {
        .lkcl-ring { animation: none !important; }
      }
    ` }));
}
